import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, addDoc, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { playSound, SOUNDS } from '../utils/audio';
import './SetManager.css';
import '../styles/Modal.css';

const SetManager = ({ userId, userData, onClose, onSelectSet, onManageCharacters }) => {
  const [sets, setSets] = useState([]);
  const [selectedSets, setSelectedSets] = useState([]);
  const [newSetName, setNewSetName] = useState('');
  const [error, setError] = useState('');

  const fetchSets = useCallback(async () => {
    try {
      // Fetch user's sets
      const userSetsQuery = query(collection(db, 'sets'), where('userId', '==', userId));
      const userSetsSnapshot = await getDocs(userSetsQuery);
      const userSets = userSetsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch demo set
      const demoSetDoc = await getDoc(doc(db, 'sets', 'demo-set'));
      let allSets = demoSetDoc.exists()
        ? [{ id: 'demo-set', ...demoSetDoc.data() }, ...userSets]
        : userSets;

      // Sort all sets alphabetically by name
      allSets = allSets.sort((a, b) => a.name.localeCompare(b.name));

      setSets(allSets);
    } catch (fetchError) {
      setError('Error fetching sets');
    }
  }, [userId]);

  useEffect(() => {
    fetchSets();
  }, [fetchSets]);

  const handleCreateSet = async (e) => {
    e.preventDefault();

    if (userData?.tier === 'free' && !userData?.admin) {
      const userSets = sets.filter(s => s.userId === userId);
      if (userSets.length >= 5) {
        setError('Free tier limited to 5 sets. Please upgrade.');
        return;
      }
    }

    if (!newSetName.trim()) {
      setError('Set name is required');
      return;
    }

    try {
      const setRef = await addDoc(collection(db, 'sets'), {
        userId,
        name: newSetName.trim(),
        createdAt: new Date()
      });

      playSound(SOUNDS.TAP);

      setNewSetName('');
      setError('');
      // Navigate to manage page for the newly created set
      onManageCharacters({ id: setRef.id, name: newSetName.trim() });
    } catch (createError) {
      setError('Error creating set');
    }
  };

  const handleSelect = async (set) => {
    if (userData?.tier === 'free' && !userData?.admin) {
      const selectedCount = selectedSets.length;
      if (selectedCount >= 5) {
        setError('Free tier limited to 5 sets. Please upgrade.');
        return;
      }
    }
    if (selectedSets.find(s => s.id === set.id)) {
      setSelectedSets(prev => prev.filter(s => s.id !== set.id));
    } else {
      setSelectedSets(prev => [...prev, set]);
    }
    playSound(SOUNDS.TAP);
  };

  return (
    <div className="set-manager">
      <h2>
        Select Sets
      </h2>
      <form onSubmit={handleCreateSet} className="create-set-form">
        <input
          type="text"
          value={newSetName}
          onChange={(e) => setNewSetName(e.target.value)}
          placeholder="New Set Name"
        />
        <button type="submit" className="btn btn-primary">Create Set</button>
      </form>

      {error && <p className="error">{error}</p>}

      <div className="sets-list">
        {sets.map(set => (
          <div
            key={set.id}
            className={`set-item ${selectedSets.find(s => s.id === set.id) ? 'selected' : ''}`}
            onClick={(e) => {
              if (e.target.closest('.set-actions')) return;
              playSound(SOUNDS.TAP);
              onSelectSet([set]);
            }}
            style={{ cursor: 'pointer' }}
          >
            <span className="set-name">
              {set.name} {set.isDemo && <span className="demo-badge">Demo</span>}
            </span>
            <div className="set-actions">
              <button
                className={`btn ${selectedSets.find(s => s.id === set.id) ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => handleSelect(set)}
              >
                {selectedSets.find(s => s.id === set.id) ? 'Selected' : 'Select'}
              </button>
              {!set.isDemo && (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    playSound(SOUNDS.TAP);
                    onManageCharacters(set);
                  }}
                >
                  Manage
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="set-manager-actions">
        <button
          className="btn btn-primary"
          disabled={selectedSets.length === 0}
          onClick={() => {
            playSound(SOUNDS.TAP);
            onSelectSet(selectedSets);
          }}
        >
          Start Practice ({selectedSets.length} sets)
        </button>
      </div>
    </div>
  );
};

export default SetManager;